import type { Point } from "./schema";
export const distance = (a: Point, b: Point) =>
  Math.hypot(b.x - a.x, b.y - a.y);
export function calibration(a: Point, b: Point, meters: number) {
  const span = distance(a, b);
  if (!Number.isFinite(meters) || meters <= 0 || span < 1)
    throw new Error("Use a positive distance and two distinct points.");
  return meters / span;
}
export function polygonArea(points: Point[]) {
  return (
    Math.abs(
      points.reduce((sum, p, i) => {
        const q = points[(i + 1) % points.length];
        return sum + p.x * q.y - q.x * p.y;
      }, 0),
    ) / 2
  );
}
export function angleDegrees(points: Point[]) {
  if (points.length < 3) return 0;
  const [a, b, c] = points,
    u = { x: a.x - b.x, y: a.y - b.y },
    v = { x: c.x - b.x, y: c.y - b.y };
  const denom = Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y);
  return denom
    ? (Math.acos(Math.max(-1, Math.min(1, (u.x * v.x + u.y * v.y) / denom))) *
        180) /
        Math.PI
    : 0;
}
export const formatLength = (n: number) => `${n.toFixed(2)} m`;
export const paperSizes = { A4: [210, 297], A3: [297, 420] } as const;
export function exportMetrics(
  w: number,
  h: number,
  metersPerUnit: number,
  scale: number,
  paper: "A3" | "A4",
  landscape: boolean,
  fullSheet = false,
) {
  if (![w, h, metersPerUnit, scale].every((n) => Number.isFinite(n) && n > 0))
    throw new Error("Calibrate the drawing before exporting to scale.");
  const [short, long] = paperSizes[paper],
    pageW = landscape ? long : short,
    pageH = landscape ? short : long;
  const mmPerUnit = (metersPerUnit * 1000) / scale;
  return {
    pageW,
    pageH,
    mmPerUnit,
    drawingW: w * mmPerUnit,
    drawingH: h * mmPerUnit,
    fits:
      w * mmPerUnit <= pageW - (fullSheet ? 0 : 20) + 1e-6 &&
      h * mmPerUnit <= pageH - (fullSheet ? 0 : 28) + 1e-6,
  };
}
